import { useInvitationEmailAttachments } from '@/hooks/use-invitation-email-attachments';
import { usePreparedInvitationAttachments } from '@/hooks/use-prepared-invitation-attachments';
import { EmailInvitationMessageEditor } from '@/components/editor/lexical/email-invitation-message-editor';
import { EmailConnectPrompt } from '@/components/integrations/EmailConnectPrompt';
import { Alert } from '@/components/ui/alert';
import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import { useInvitationData, useInvitationStoreSelector } from '@/contexts/invitation-context';
import { usePrepareTemplate } from '@/hooks/actions';
import { useEmailIntegrationStatus } from '@/hooks/use-email-integration-status';
import { validateRequiredVariables } from '@/libs/utils/lexical-utils';
import { InvitationMethod } from '@/types/invitation';
import { AlertDescription, Button, Text, VStack } from '@chakra-ui/react';
import { TemplateType } from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiPersonalizeToggle, MessageLanguageToggle } from './components';
import InvitationMessageCardSkeleton from './components/invitation-message-card-skeleton';
import { useOutreachEmailSubject } from './outreach/use-outreach-subject';
import { useOutreachSubjectState } from './outreach/use-outreach-subject-state';
import { resolveBulkBaseTemplateId } from './templates/bulk-base-template';
import {
  resolveInvitationEditorContent,
  resolveInvitationEditorKey,
} from './templates/invitation-editor-content';
import { useDetailsMessageWiring } from './templates/use-details-message-wiring';
import { useBulkInviteSubmit } from './use-bulk-invite-submit';

interface BulkWishlistDetailsViewProps {
  onClose: ({ success }: { success?: boolean }) => void;
}

/**
 * Email mode of the bulk affiliation delivery step: the outreach email composed
 * once for every selected wishlist creator, sent from the user's connected
 * mailbox (symmetry with the mono email channel and the Favorites bulk
 * outreach). Gates on a usable mailbox (connect prompt otherwise), adds an
 * editable subject pre-filled in the body language, and keeps the editor,
 * variables, language toggle and AI personalization. The send routes to the
 * `bulkProgress` epilogue via the shared submit hook; the recipient banner and
 * the mail-less warning live on the parent delivery view.
 */
const BulkWishlistDetailsView: React.FC<BulkWishlistDetailsViewProps> = memo(({ onClose }) => {
  const { t } = useTranslation();
  const files = useInvitationEmailAttachments();
  const { user } = useAuth();
  const { target, selectedProgram } = useInvitationData();
  const aiPersonalize = useInvitationStoreSelector((state) => state.aiPersonalize);
  const setAiPersonalize = useInvitationStoreSelector((state) => state.setAiPersonalize);

  // Strict send capability: a mailbox needing a reconnect cannot send, so it
  // gates the compose exactly like the mono email and Favorites bulk flows do.
  const { hasUsableMailbox, isLoading: isIntegrationLoading } = useEmailIntegrationStatus();

  const wiring = useDetailsMessageWiring(InvitationMethod.EMAIL, files.replaceAttachments);
  const {
    messageSnapshot,
    compiledMessage,
    originalTemplate,
    setMessageSnapshot,
    setOriginalTemplate,
    resetMessage,
    setMessageLanguage,
    languageToggleEnabled,
    selectedLanguage,
    overrideLanguage,
    isEditorUntouched,
    templateFlow,
    buildEditorFooter,
    saveAsTemplatePanel,
    handleEditorChange,
    handleLanguageToggle,
  } = wiring;

  // Track if we've initialized the email template from backend
  const [emailTemplateInitialized, setEmailTemplateInitialized] = useState(false);

  const { data: emailTemplateData, isLoading: isLoadingEmailTemplate } = usePrepareTemplate(
    selectedProgram?.id || '',
    TemplateType.CREATOR_OUTREACH_EMAIL_INVITATION,
    {
      enabled: !!selectedProgram?.id && hasUsableMailbox,
      language: overrideLanguage,
    }
  );

  usePreparedInvitationAttachments(emailTemplateData?.template?.attachments);

  const { submit, isSubmitting } = useBulkInviteSubmit();

  // When the library has at least one email/invitation entry, the selector owns
  // the body. The backend-prepared template only fills in as a fallback when
  // the brand has no email templates at all.
  const bulkUsesLibraryTemplate = templateFlow.entries.length > 0;

  // Overwrite the editor body only when the current snapshot still matches the
  // previously-loaded template — preserves manual edits across language switches.
  // `originalTemplate` tracks the latest locale so "reset" restores it.
  useEffect(() => {
    if (bulkUsesLibraryTemplate) return;
    const templateContent = emailTemplateData?.template?.contentLexicalJson;
    if (!templateContent) return;

    const isUntouched = !messageSnapshot || messageSnapshot === originalTemplate;
    if (isUntouched) {
      setMessageSnapshot(templateContent);
    }
    setOriginalTemplate(templateContent);
    if (!emailTemplateInitialized) {
      setEmailTemplateInitialized(true);
    }
  }, [
    bulkUsesLibraryTemplate,
    emailTemplateData,
    messageSnapshot,
    originalTemplate,
    emailTemplateInitialized,
    setMessageSnapshot,
    setOriginalTemplate,
  ]);

  const emailEditorKey = resolveInvitationEditorKey({
    language: emailTemplateData?.template?.language,
    selectedEntryId: templateFlow.selectedEntryId,
  });
  const emailEditorContent = resolveInvitationEditorContent({
    isEditorUntouched,
    selectedEntryId: templateFlow.selectedEntryId,
    messageSnapshot,
    fallbackContent: emailTemplateData?.template?.contentLexicalJson,
  });

  // Variables include disabled state from the backend response.
  const availableVariables: AvailableVariable[] = useMemo(() => {
    if (!emailTemplateData?.variables) return [];
    return emailTemplateData.variables.map((variable) => ({
      key: variable.key,
      label: variable.label,
      labelKey: variable.labelKey,
      description: variable.description,
      descriptionKey: variable.descriptionKey,
      required: variable.required,
      value: variable.value || '{{auto}}',
      disabled: variable.disabled,
      disabledReason: variable.disabledReason
        ? t(`invitations.editor.${variable.disabledReason}`)
        : undefined,
    }));
  }, [emailTemplateData, t]);

  // Editable subject with variable chips, seeded from the localized default
  // resolved in the BODY language. Shared primitive with the mono flows: a
  // language switch re-seeds the subject and drops a manual edit, mirroring the
  // body's own re-seed on an untouched template. Deliberately not frozen while
  // the template loads: the seed is language-derived, independent of the fetch.
  const defaultSubject = useOutreachEmailSubject(user?.currentBrand?.name ?? '', 'affiliation');
  const { subjectRow, subject, isSubjectValid } = useOutreachSubjectState(
    defaultSubject,
    availableVariables
  );

  // Validate required variables in the message
  const validationResult = useMemo(() => {
    if (!messageSnapshot) {
      return { isValid: true, missingVariables: [] };
    }
    return validateRequiredVariables(messageSnapshot, availableVariables);
  }, [messageSnapshot, availableVariables]);

  // Handle reset to original template
  const handleReset = useCallback(() => {
    resetMessage();
    setEmailTemplateInitialized(false);
  }, [resetMessage]);

  const wishlistItems = target?.type === 'wishlist-bulk' ? target.data : [];

  // Handle send for bulk wishlist invitations (email mode)
  const handleBulkSend = useCallback(async () => {
    const trimmedSubject = subject.trim();
    if (
      target?.type !== 'wishlist-bulk' ||
      !selectedProgram.id ||
      !isSubjectValid ||
      files.isBlocked
    ) {
      toaster.create({
        title: t('invitations.modal.invitationDetails.missingData'),
        type: 'error',
        duration: 3000,
      });
      return;
    }

    // Base template attribution: the selected brand template id, null for a
    // system default, or omitted entirely when the pair has no library entry.
    const baseTemplateId = resolveBulkBaseTemplateId(
      bulkUsesLibraryTemplate,
      templateFlow.selectedTemplateId
    );

    await submit({
      wishlistItemIds: target.data.map((item) => item.id),
      programId: selectedProgram.id,
      deliveryMode: 'email',
      attachmentIds: files.attachments.map((file) => file.id),
      subject: trimmedSubject,
      customMessage: messageSnapshot || undefined,
      compiledMessage: compiledMessage || undefined,
      aiPersonalizationEnabled: aiPersonalize,
      ...(languageToggleEnabled ? { messageLanguage: selectedLanguage } : {}),
      ...(baseTemplateId !== undefined ? { baseTemplateId } : {}),
    });
  }, [
    subject,
    files.attachments,
    files.isBlocked,
    isSubjectValid,
    target,
    selectedProgram,
    messageSnapshot,
    compiledMessage,
    aiPersonalize,
    languageToggleEnabled,
    selectedLanguage,
    bulkUsesLibraryTemplate,
    templateFlow.selectedTemplateId,
    submit,
    t,
  ]);

  // No usable mailbox: prompt to connect instead of a composer that cannot send.
  if (!isIntegrationLoading && !hasUsableMailbox) {
    return (
      <VStack align="stretch" w="full" gap={4}>
        <EmailConnectPrompt />
      </VStack>
    );
  }

  return (
    <VStack align="stretch" w="full" gap={4}>
      {subjectRow}

      {isLoadingEmailTemplate ? (
        <InvitationMessageCardSkeleton />
      ) : (
        emailEditorContent && (
          <EmailInvitationMessageEditor
            attachmentControl={{
              attachments: files.attachments,
              onFileSelect: files.handleFileSelect,
              onRemoveAttachment: files.handleRemoveAttachment,
              isUploading: files.isUploading,
              deletingId: files.deletingId,
              issues: files.issues,
              onDismissIssue: files.dismissIssue,
              validationError: files.validationError,
              maxTotalBytes: files.maxTotalBytes,
            }}
            key={emailEditorKey}
            initialContent={emailEditorContent}
            availableVariables={availableVariables}
            onContentChange={handleEditorChange}
            onReset={handleReset}
            footerTrailing={buildEditorFooter(isLoadingEmailTemplate)}
          />
        )
      )}

      {!validationResult.isValid && (
        <Alert status="error" colorPalette="red" p={4} rounded="lg">
          <AlertDescription>
            <Text>
              {t('invitations.editor.missingRequiredVariable', {
                variable: `{{${validationResult.missingVariables[0]}}}`,
              })}
            </Text>
          </AlertDescription>
        </Alert>
      )}

      <MessageLanguageToggle
        enabled={languageToggleEnabled}
        value={selectedLanguage}
        onToggle={handleLanguageToggle}
        onChange={setMessageLanguage}
        disabled={isLoadingEmailTemplate}
      />

      <AiPersonalizeToggle
        checked={aiPersonalize}
        onCheckedChange={({ checked }) => setAiPersonalize(checked)}
        creatorCount={wishlistItems.length}
        disabled={isLoadingEmailTemplate}
      />

      <Button
        w="full"
        colorPalette="primary"
        onClick={handleBulkSend}
        loading={isSubmitting}
        disabled={
          !messageSnapshot ||
          !validationResult.isValid ||
          !isSubjectValid ||
          isLoadingEmailTemplate ||
          files.isBlocked
        }
      >
        {aiPersonalize
          ? t('invitations.modal.invitationDetails.sendBulkInvitationsAi', {
              count: wishlistItems.length,
            })
          : t('invitations.modal.invitationDetails.sendBulkInvitations', {
              count: wishlistItems.length,
            })}
      </Button>

      {saveAsTemplatePanel}
    </VStack>
  );
});

BulkWishlistDetailsView.displayName = 'BulkWishlistDetailsView';

export default BulkWishlistDetailsView;
